import { alphaOf } from '../utils/ui/colorAlpha';
import { semantic } from '../ui/theme/semanticTokens';
import { secondaryFigureStyle } from '../ui/figureType';
import {
  foundation,
  buttonPrimary as primaryButton,
  buttonSecondary as secondaryButton,
  dataSectionCard as sectionCard,
  mobileSectionCard,
  emptyState as emptyStateBlock,
} from '../ui';
import { ui } from './logJobData';
import { LOGJOB_COLUMN_MAX_WIDTH } from './logJobLayoutStyles';
import { PHONE_GUTTER_LEFT_CSS, PHONE_GUTTER_RIGHT_CSS } from '../ui/pageGutter';
// The 'bar' variant's two-row total row/action-row styles + the derived
// LOGJOB_MOBILE_BAR_HEIGHT_PX constant live in their own file — see its
// header for the full arithmetic — so this file stays under the 500-line cap.
import { LOGJOB_MOBILE_BAR_HEIGHT_PX, LOGJOB_MOBILE_BAR_ABOVE_PAD_PX } from './logJobBarStyles';

const styles = {
  container: {
    height: '100dvh',
    maxHeight: '100dvh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: semantic.bg.canvas,
  },
  scrollWrap: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
  },
  // Mobile only: bottomBar is position:fixed (so it stays anchored to the
  // visual viewport when iOS opens the keyboard). Reserve scroll-clear space
  // so the last content row isn't hidden behind the fixed bar.
  scrollWrapMobile: {
    // KAN-1069: the bar's MEASURED height (note included; LogJobBottomBar publishes it on the page root).
    // The hand-computed sum is only the first-paint fallback (and the skeleton's, which has no bar).
    paddingBottom: `var(--cvx-logjob-bar-h, calc(${LOGJOB_MOBILE_BAR_ABOVE_PAD_PX}px + max(12px, env(safe-area-inset-bottom, 0px))))`,
  },
  backButton: {
    ...secondaryButton,
    padding: '8px 14px',
    borderRadius: '10px',
    fontSize: ui.typography.fontSize.sm,
    fontWeight: ui.typography.fontWeight.medium,
    border: `1px solid ${semantic.border.strong}`,
    backgroundColor: semantic.bg.surface,
    color: semantic.fg.default,
  },
  content: {
    padding: '26px 24px 34px',
    // The form column width — shared with the bottom bar's inner row via one
    // constant (logJobLayoutStyles) so the two can never drift.
    maxWidth: `${LOGJOB_COLUMN_MAX_WIDTH}px`,
    margin: '0 auto',
  },
  contentMobile: {
    paddingTop: '14px',
    // Phone side margin = the app-wide gutter (pageGutter.js, KAN-1006); the bottom bar uses the same one.
    paddingRight: PHONE_GUTTER_RIGHT_CSS,
    paddingLeft: PHONE_GUTTER_LEFT_CSS,
    paddingBottom: '12px',
    boxSizing: 'border-box',
    /* Avoid 300ms tap delay / double-tap zoom wait on WebKit; helps first-tap focus on inputs */
    touchAction: 'manipulation',
  },
  heroCard: {
    background: `linear-gradient(145deg, ${semantic.bg.surface} 0%, ${semantic.bg.canvas} 100%)`,
    border: `1px solid ${semantic.border.default}`,
    borderRadius: '20px',
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.06)',
    padding: '22px 24px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '20px',
    alignItems: 'flex-start',
  },
  heroCardMobile: {
    display: 'block',
    padding: '14px',
    marginBottom: '8px',
  },
  title: {
    color: semantic.fg.default,
    margin: '0 0 8px 0',
    fontSize: ui.typography.fontSize['2xl'],
    fontWeight: ui.typography.fontWeight.bold,
    fontFamily: ui.typography.fontFamily,
  },
  titleMobile: {
    fontSize: ui.typography.fontSize.xl,
  },
  customerName: {
    color: ui.colors.text,
    fontWeight: ui.typography.fontWeight.bold,
    fontSize: ui.typography.fontSize.lg,
    marginBottom: '6px',
    fontFamily: ui.typography.fontFamily,
  },
  customerNameMobile: {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  address: {
    color: ui.colors.textMuted,
    margin: 0,
    lineHeight: 1.5,
    fontSize: ui.typography.fontSize.base,
    fontFamily: ui.typography.fontFamily,
  },
  addressMobile: {
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
  section: {
    ...sectionCard,
    padding: '16px 20px',
    marginBottom: '12px',
  },
  sectionMobile: {
    ...mobileSectionCard,
    padding: '12px 14px',
    marginBottom: '10px',
  },
  sectionTitle: {
    color: semantic.fg.default,
    margin: 0,
    fontSize: ui.typography.fontSize.xl,
    fontWeight: ui.typography.fontWeight.semibold,
    fontFamily: ui.typography.fontFamily,
  },
  emptyState: {
    ...emptyStateBlock,
    marginTop: -8,
    padding: '0 16px',
    fontSize: ui.typography.fontSize.sm,
  },
  // Compact picker-driven card: tight padding, auto-height, no inline dropdown.
  lineCardCompact: { padding: '12px 14px', gap: '6px', boxShadow: 'none' },
  lineCardCompactRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', minWidth: 0 },
  lineCardCompactName: {
    display: 'inline-flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1,
    fontSize: ui.typography.fontSize.base, fontWeight: ui.typography.fontWeight.semibold,
    color: ui.colors.text, fontFamily: ui.typography.fontFamily,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  lineCardCompactPrice: {
    fontSize: ui.typography.fontSize.base, fontWeight: ui.typography.fontWeight.bold,
    color: semantic.fg.default, fontFamily: ui.typography.fontFamily, whiteSpace: 'nowrap', flexShrink: 0,
  },
  lineCardCompactSubline: {
    fontSize: ui.typography.fontSize.xs, color: ui.colors.textMuted,
    fontFamily: ui.typography.fontFamily, margin: 0,
  },
  removeIconBtn: {
    appearance: 'none', background: 'transparent', border: 'none', color: semantic.ink.dangerVivid,
    padding: '6px', margin: 0, borderRadius: '8px', cursor: 'pointer', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '36px', minWidth: '36px', WebkitTapHighlightColor: 'transparent',
  },
  qtyInputCompact: {
    width: '72px', minHeight: '36px', padding: '6px 10px',
    border: `1px solid ${semantic.border.strong}`, borderRadius: '8px',
    fontSize: '16px', fontWeight: 600, fontFamily: ui.typography.fontFamily,
    color: semantic.fg.default, backgroundColor: semantic.bg.surface, textAlign: 'center',
    outline: 'none', touchAction: 'manipulation',
  },
  lineCardTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '8px',
  },
  lineCardLabel: {
    fontSize: ui.typography.fontSize.sm,
    fontWeight: ui.typography.fontWeight.semibold,
    color: ui.colors.text,
    fontFamily: ui.typography.fontFamily,
  },
  lineCardBottomRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: '14px',
    marginTop: '8px',
  },
  lineCardBottomRowMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  mobileFieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '8px',
  },
  mobileFieldRowStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  mobileFieldRowSplit: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginTop: '8px',
    alignItems: 'start',
  },
  /** Match chemical line: qty + price on separate rows on mobile for tap targets and consistent rhythm. */
  mobileFieldRowSplitStacked: {
    gridTemplateColumns: '1fr',
    gap: '12px',
  },
  priceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '100px',
    alignSelf: 'flex-end',
    textAlign: 'center',
    fontWeight: ui.typography.fontWeight.bold,
    color: semantic.fg.default,
    backgroundColor: semantic.bg.surfaceMuted,
    border: `1px solid ${semantic.border.strong}`,
    borderRadius: ui.radius.pill,
    padding: '12px 16px',
    fontSize: ui.typography.fontSize.sm,
    fontFamily: ui.typography.fontFamily,
  },
  priceBadgeMobile: {
    minWidth: '100px',
  },
  priceBadgeMobileFull: {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '8px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: ui.colors.text,
    fontWeight: ui.typography.fontWeight.medium,
    // foundation, NOT ui.typography — the two scales disagree on `sm`
    // (foundation 0.8125rem vs legacy 0.875rem). Form labels follow the
    // modern scale so every drawer in the app reads at one size.
    fontSize: foundation.fontSize.sm,
    fontFamily: ui.typography.fontFamily,
  },
  bottomBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexShrink: 0,
    paddingTop: '14px',
    paddingRight: '24px',
    paddingBottom: '14px',
    paddingLeft: '24px',
    backgroundColor: semantic.tint.slate25,
    borderTop: `1px solid ${alphaOf(semantic.fg.default, 0.06)}`,
    boxShadow: '0 -6px 18px rgba(15, 23, 42, 0.04)',
  },
  bottomBarMobile: {
    // Anchor to the visual viewport so iOS keyboard auto-scroll doesn't
    // expose the body background under the bar (matches BottomTabBar).
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    // Ties the bar's own floor to the same figure scrollWrapMobile reserves
    // — see LOGJOB_MOBILE_BAR_HEIGHT_PX above. A safety floor, not a driver:
    // the real height comes from the paddings/row-heights below, which sum
    // to the same 79.
    minHeight: `${LOGJOB_MOBILE_BAR_HEIGHT_PX}px`,
    paddingTop: '10px',
    paddingRight: PHONE_GUTTER_RIGHT_CSS,
    paddingLeft: PHONE_GUTTER_LEFT_CSS,
    /* The larger of the 12px gutter and the home-indicator inset -- never their
       sum (KAN-768): the sum left a ~46px dead band under the button. */
    paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '8px',
    // Solid surface (KAN-785): a translucent + blurred bar let the content
    // ghost through behind the total and the button.
    backgroundColor: foundation.color.neutral.surface,
    borderTop: `1px solid ${alphaOf(semantic.fg.default, 0.08)}`,
  },
  // ── 'rail' variant (desktop) total block — unchanged stacked shape ──────
  // The 'bar' variant's (phone/tablet) two-row total row lives in
  // logJobBarStyles.js's `barStyles` — imported directly by
  // LogJobSubmitBar.js, not re-exported from here.
  totalBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  totalBlockMobile: {
    gap: '4px',
  },
  totalLabel: {
    color: ui.colors.textMuted,
    fontWeight: ui.typography.fontWeight.medium,
    fontSize: ui.typography.fontSize.sm,
    fontFamily: ui.typography.fontFamily,
  },
  totalText: {
    ...secondaryFigureStyle,
    color: semantic.fg.default,
  },
  totalTextMobile: {
    /* Same compact figure on phone and web (KAN-1141) */
    ...secondaryFigureStyle,
  },
  submitButton: {
    ...primaryButton,
    background: ui.colors.primary,
    border: `1px solid ${ui.colors.primary}`,
    width: '100%',
    maxWidth: '340px',
    minHeight: '44px',
    padding: '16px 24px',
    fontSize: ui.typography.fontSize.lg,
    fontWeight: ui.typography.fontWeight.semibold,
    boxShadow: '0 8px 20px rgba(28, 90, 165, 0.25)',
  },
  submitButtonMobile: {
    maxWidth: 'none',
    width: '100%',
    /* 48px keeps a comfortable tap target above the 44px floor */
    minHeight: '48px',
    padding: '13px 20px',
    fontSize: ui.typography.fontSize.base,
    boxShadow: ui.shadow.strong,
  },
  customPriceBadge: {
    margin: 0,
    fontSize: ui.typography.fontSize.sm,
    color: ui.colors.success,
    fontWeight: ui.typography.fontWeight.bold,
    lineHeight: 1.4,
    fontFamily: ui.typography.fontFamily,
  },
};

// NOTE (2026-09-11): `sectionHeaderStyle` — the iOS-grouped UPPERCASE eyebrow
// that used to sit above each section card — was deleted here. It was written
// for a grouped-list card that carries NO header of its own, but every Log Job
// section also rendered an <h3> resolving to the SAME i18n string, so each
// section printed its name twice (12px muted caps, then 20px near-black, 4px
// apart). The section name now lives once, in JobSection's header (inside the card).
// Do not reintroduce an eyebrow above a card that already titles itself.

export { styles };
